import { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import hero1 from "../assets/hero1.webp";
import hero2 from "../assets/hero2.jpg";
import hero3 from "../assets/hero3.jpg";
import hero4 from "../assets/hero4.jpeg";

import g1 from "../assets/hero1.webp";
import g2 from "../assets/hero2.jpg";
import g3 from "../assets/hero3.jpg";
import g4 from "../assets/hero4.jpeg";

function Home() {
  const [current, setCurrent] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const navigate = useNavigate();

  const [selectedImg, setSelectedImg] = useState(null);

  const images = [g1, g2, g3, g4];

  const slides = [
    {
      image: hero1,
      title: "Blue Lotus Cabana",
      subtitle: "Escape to Serenity",
    },
    {
      image: hero2,
      title: "Nature Meets Luxury",
      subtitle: "Awake to serene breezes and lush Blue Lotus surroundings",
    },
    {
      image: hero3,
      title: "Relax & Unwind",
      subtitle: "Peaceful stays designed for total comfort",
    },
    {
      image: hero4,
      title: "Your Serene Escape at Blue Lotus Cabana",
      subtitle: "Unwind in elegance, amidst tropical bliss",
    },
  ];

  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setShowContent(false);
    const t = setTimeout(() => setShowContent(true), 700);
    return () => clearTimeout(t);
  }, [current]);

  const containerVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1, ease: "easeOut", staggerChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  return (
    <div className="w-full overflow-hidden">

      {/* ================= HERO SECTION ================= */}
      <section className="relative min-h-[80vh] sm:min-h-[85vh] md:min-h-[90vh] w-full overflow-visible">

        {/* Slides */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${index === current ? "opacity-100" : "opacity-0"
              }`}
            style={{ backgroundImage: `url(${slide.image})` }}
          />
        ))}

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Hero Content */}
        <div className="absolute inset-0 flex items-center justify-center px-4 z-10">
          <motion.div
            key={current}
            variants={containerVariants}
            initial="hidden"
            animate={showContent ? "visible" : "hidden"}
            className="bg-black/50 backdrop-blur-sm border border-yellow-500/60 px-6 py-10 text-center text-white sm:px-10 md:px-16 md:py-14"
          >
            <motion.h1
              variants={itemVariants}
              className="mb-6 text-3xl font-bold sm:text-4xl md:text-5xl lg:text-5xl"
            >
              {slides[current].title}
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="mb-8 text-gray-400 leading-relaxed text-base sm:text-lg md:text-xl"
            >
              {slides[current].subtitle}
            </motion.p>

            <motion.button
              variants={itemVariants}
              onClick={() => navigate("/")}
              className="mt-4 bg-[#b8742c] px-8 py-3 text-white tracking-wide hover:bg-[#9e6325] transition"
            >
              Read More
            </motion.button>
          </motion.div>
        </div>

        {/* ================= OVERLAY FEATURES ================= */}
        <div className="absolute -bottom-14 left-1/2 -translate-x-1/2 z-30 w-full max-w-6xl px-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-4">
            {[
              { img: "cocktail.png", label: "Cocktails" },
              { img: "pool.png", label: "Pools" },
              { img: "room.png", label: "Rooms" },
              { img: "cabana.png", label: "Cabanas" },
            ].map((item, i) => (
              <div
                key={i}
                className="group flex flex-col items-center justify-center border border-[#c97b3a] bg-white/80 py-3 sm:py-4 md:py-6 transition hover:bg-[#c97b3a]"
              >
                <img
                  src={require(`../assets/${item.img}`)}
                  alt={item.label}
                  className="mb-2 h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 opacity-90"
                />
                <p className="text-sm sm:text-base md:text-lg font-medium tracking-wide text-gray-800 group-hover:text-white">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>

      </section>

      {/* ================= ABOUT RESORT SECTION ================= */}
      <section className="relative bg-white py-24 sm:py-28 lg:py-32 px-4 sm:px-6 lg:px-16">
        <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* LEFT CONTENT */}
          <div className="text-center lg:text-left">
            <span className="block w-20 h-[2px] bg-orange-400 mx-auto mb-4"></span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              Blue Lotus Cabana
            </h2>

            <p className="mt-2 text-lg text-gray-500 italic">
              Where Legends Unfold
            </p>

            <p className="mt-6 text-gray-600 leading-relaxed text-base sm:text-lg md:text-xl">
              Chill by the pool, practice yoga, savor local flavors, and soak in nature's calm. Cozy A-frame cabana for couples & families amidst lush greenery and a blue lotus pond 🌸, in Kalutara, Sri Lanka.
            </p>

            <p className="mt-4 text-gray-600 leading-relaxed text-base sm:text-lg md:text-xl">
              Blue Lotus Cabana is conveniently situated near the historic center of Kalutara and is enveloped by a lush forest reserve, home to a rich variety of native wildlife.
            </p>

            <button
              onClick={() => navigate("/about")}
              className="mt-8 bg-[#b8742c] px-8 py-3 text-white tracking-wide hover:bg-[#9e6325] transition">
              Read More
            </button>
          </div>

          {/* RIGHT IMAGE COLLAGE */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative w-[280px] sm:w-[360px] md:w-[450px] lg:w-[500px]">

              {/* Main Image */}
              <img
                src={require("../assets/resort1.jpg")}
                alt="Resort View"
                className="rounded-md shadow-xl w-full"
              />

              {/* Top Image */}
              <img
                src={require("../assets/resort2.jpg")}
                alt="Resort Entrance"
                className="absolute -top-6 -right-6 w-28 sm:w-32 md:w-40 lg:w-44 rounded-md shadow-lg"
              />

              {/* Bottom Image */}
              <img
                src={require("../assets/resort3.jpg")}
                alt="Traditional Art"
                className="absolute -bottom-6 left-2 w-28 sm:w-32 md:w-40 lg:w-44 rounded-md shadow-lg"
              />
            </div>
          </div>

        </div>
      </section>

      {/* ================= VISION SECTION ================= */}
      <section
        className="relative h-[55vh] w-full bg-fixed bg-center bg-cover flex items-center justify-center"
        style={{
          backgroundImage: `url(${require("../assets/vision-bg.jpg")})`,
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60"></div>

        {/* Content */}
        <div
          className="relative z-10 max-w-4xl px-6 text-center text-white"
          data-aos="fade-up"
          data-aos-duration="1200"
        >
          <span className="block w-20 h-[2px] bg-orange-400 mx-auto mb-4"></span>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide">
            Our Vision
          </h2>

          <p className="mt-6 text-white leading-relaxed text-base sm:text-lg md:text-xl">
            To share the richness of rural Sri Lankan life with both international and local visitors, enriching their holiday experience while sparking cultural curiosity. We are committed to providing every guest with an exciting, relaxing, and high-quality stay in a refined setting, complemented by innovative cuisine and personalized service.
            <br /><br />
            We aim to create an experience that delights the senses, nurtures well-being, and fulfills even unspoken desires—leaving warm, lasting memories that invite guests to return year after year.
          </p>
        </div>
      </section>

      {/* ================= WONDERLAND SRI LANKA SECTION ================= */}
      <section className="bg-white py-10 sm:py-12 px-4 sm:px-6 lg:px-16">
        <div className="mx-auto max-w-7xl text-center">
          <span className="block w-20 h-[2px] bg-orange-400 mx-auto mb-4"></span>
          {/* Section Title */}
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-800">
            Wonderland Sri Lanka
          </h2>

          <p className="mt-6 text-gray-600 leading-relaxed text-base sm:text-lg md:text-xl">
            Experience the Blue Lotus escape in Kalutara. Plush cabanas, stunning views, personalized service, and tropical charm let you unwind in an authentic village setting. Explore Kalutara Bodhiya, golden beaches, blue lotus blooms, Richmond Castle’s colonial charm, and catch breathtaking sunsets – soak up the island vibe
          </p>

          {/* Cards */}
          <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">

            {/* Card */}
            {[
              {
                img: require("../assets/kalutara-bodhiya.jpg"),
                title: "Kalutara Bodhiya",
                text: "Kalutara Bodhiya is one of the most sacred Buddhist sites...",
                path: "/kalutara-bodhiya",
              },
              {
                img: require("../assets/Kalutara-beach.jpg"),
                title: "Kalutara Beach",
                text: "Kalutara Beach is a popular coastal destination...",
                path: "/kalutara-beach",
              },
              {
                img: require("../assets/Richmond castle Kalutara.jpg"),
                title: "Richmond Castle Kalutara",
                text: "Richmond Castle is a historic Edwardian-era mansion...",
                path: "/richmond-castle",
              },
            ].map((card, i) => (
              <div key={i} className="relative group overflow-hidden rounded-lg">

                {/* Image */}
                <img
                  src={card.img}
                  alt={card.title}
                  className="
                    h-[320px] sm:h-[420px] lg:h-[500px]
                    w-full object-fixed
                    transition-transform duration-700
                    md:group-hover:scale-110
                  "
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/55 flex flex-col justify-end p-4 sm:p-6">
                  <div className="border border-[#c97b3a] p-4 sm:p-6 text-white text-center">

                    <h3 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3">
                      {card.title}
                    </h3>

                    <p className="mt-6 text-white leading-relaxed text-base sm:text-lg md:text-xl">
                      {card.text}
                    </p>

                    <button
                      onClick={() => navigate(card.path)}
                      className="
                        mt-4 sm:mt-6
                        bg-[#c97b3a]
                        px-5 py-2
                        text-xs sm:text-sm
                        tracking-wide
                        hover:bg-[#b26a30]
                        transition
                      "
                    >
                      Read More
                    </button>

                  </div>
                </div>

              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CULINARY EXPERIENCE SECTION ================= */}
      <section
        className="
          relative w-full h-[50vh] sm:h-[60vh]
          bg-center bg-cover flex items-center
          md:bg-fixed
        "
        style={{
          backgroundImage: `url(${require("../assets/food-bg.jpg")})`,
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50"></div>

        {/* Content */}
        <div className="relative z-10 w-full flex justify-center md:justify-end px-4 sm:px-10 lg:px-20">
          <div
            className="max-w-xl border border-[#c97b3a] bg-black/40 backdrop-blur-sm p-6 sm:p-10 text-white"
            data-aos="fade-up"
          >
            <span className="block h-[2px] w-16 bg-[#c97b3a] mb-4"></span>

            <h2 className="text-2xl sm:text-5xl md:text-4xl font-bold mb-4">
              Stuffing Oneself
            </h2>

            <p className="mt-6 text-white leading-relaxed text-base sm:text-lg md:text-xl">
              Savor local flavors & sunset dinners at Blue Lotus Cabana 🍴
            </p>

            <button
              onClick={() => navigate("/dining")}
              className="mt-6 bg-[#c97b3a] px-6 py-2 text-xs sm:text-sm tracking-widest uppercase hover:bg-[#b26a30]"
            >
              See More
            </button>
          </div>
        </div>
      </section>

      {/* ================= ACCOMMODATION SECTION ================= */}
      <section className="bg-white py-10 sm:py-10 px-4 sm:px-6 lg:px-16">
        <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">

          {/* LEFT CONTENT */}
          <div data-aos="fade-right">
            <span className="block h-[2px] w-16 bg-[#c97b3a] mb-4"></span>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800">
              Accommodation
            </h2>

            <p className="mt-6 text-gray-600 leading-relaxed text-base sm:text-lg md:text-xl">
              Blue Lotus Cabana features seven exquisitely designed rooms that blend modern boutique living with hints of ancient architecture and heritage. Each room harmonizes with the surrounding natural environment, offering a unique experience that combines eco-lodging with luxurious comfort.
            </p>

            <button
              onClick={() => navigate("/accommodation")}
              className="mt-8 bg-[#c97b3a] px-8 py-3 text-xs sm:text-sm tracking-widest uppercase text-white hover:bg-[#b26a30] transition"
            >
              Read More
            </button>
          </div>

          {/* RIGHT IMAGE */}
          <div
            className="relative w-full h-[300px] sm:h-[380px] md:h-[420px] lg:h-[460px]"
            data-aos="fade-left"
          >
            <img
              src={require("../assets/accommodation.jpg")}
              alt="Luxury Accommodation"
              className="w-full h-full object-fixed rounded-md shadow-xl"
            />
          </div>

        </div>
      </section>

      {/* ================= GALLERY ================= */}
      <section className="bg-white py-8 sm:py-10 px-4 sm:px-6 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <span className="block w-20 h-[2px] bg-orange-400 mx-auto mb-4"></span>
          <h2
            className="text-center text-3xl sm:text-5xl md:text-5xl font-bold text-gray-800 mb-6"
            data-aos="fade-up"
          >
            Blue Lotus Cabana
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {images.map((img, index) => (
              <div
                key={index}
                className="relative group overflow-hidden rounded-md shadow-md cursor-pointer"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
                onClick={() => setSelectedImg(img)}
              >
                <img
                  src={img}
                  alt={`Blue Lotus Cabana Kalutara ${index + 1}`}
                  className="w-full h-[250px] sm:h-[400px] md:h-[300px] object-fixed transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Popup Modal */}
        {selectedImg && (
          <div
            className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4"
            onClick={() => setSelectedImg(null)}
          >
            <div className="relative">
              {/* Close Button */}
              <button
                onClick={() => setSelectedImg(null)}
                className="absolute -top-4 -right-4 bg-white text-black rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold shadow-lg hover:bg-gray-200 transition z-50"
              >
                &times;
              </button>

              <img
                src={selectedImg}
                alt="Expanded"
                onClick={(e) => e.stopPropagation()}
                className="rounded-lg shadow-lg
                   w-full
                   max-w-md sm:max-w-lg md:max-w-2xl lg:max-w-3xl
                   max-h-[80vh] object-contain"
              />
            </div>
          </div>
        )}

      </section>

      {/* ================= WHATSAPP BUTTON ================= */}
      <a
        href="https://wa.me/94705108633?text=I%20saw%20your%20website.%20Can%20you%20give%20me%20information%20regarding%20your%20Cabana."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50"
      >
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/WhatsApp.svg/512px-WhatsApp.svg.png"
          alt="WhatsApp"
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg transition hover:scale-110"
        />
      </a>

    </div>
  );
}

export default Home;
